import type { ReactNode } from "react";
import { useId, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import type { AssistantInvitationInfo } from "../../../api/assistantInvitations";
import {
  isInvalidCodeError,
  isInvitationClosedError,
  requestInvitationCodeMutationOptions,
  verifyInvitationCodeMutationOptions,
} from "../../../api/assistantInvitations";
import * as FormDialog from "../../../shared/FormDialog";

const CODE_LENGTH = 6;

type InviteLoginProps = { invitationId: string; invitation: AssistantInvitationInfo };

export const InviteLogin = ({ invitationId, invitation }: InviteLoginProps) => {
  const { t, formatDate } = useTranslation();
  const requestCodeMutation = useMutation(requestInvitationCodeMutationOptions(invitationId));
  const [isCodeSent, setIsCodeSent] = useState(false);

  const requestCode = () => requestCodeMutation.mutate(undefined, { onSuccess: () => setIsCodeSent(true) });

  return (
    <InviteFrame title={t("Assistant.Invite.Login.Title", { inviter: invitation.inviterName })}>
      <Subtitle>
        {t("Assistant.Invite.Login.Subtitle", {
          inviter: invitation.inviterName,
          organisation: invitation.organisationName,
        })}{" "}
        {t("Assistant.Invite.Login.ExpiresAt", { time: formatDate(invitation.expiresAt, { showTime: true }) })}
      </Subtitle>
      <Box sx={{ mt: 3 }}>
        {isCodeSent ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 4 }}>
              <Subtitle>{t("Assistant.Invite.Login.CodeSent", { email: invitation.maskedEmail })}</Subtitle>
              <Button
                variant="text"
                sx={{ p: 0, lineHeight: 1, textTransform: "none" }}
                loading={requestCodeMutation.isPending}
                onClick={requestCode}
              >
                {t("Assistant.Invite.Login.ResendCode")}
              </Button>
            </Box>
            <CodeStep invitationId={invitationId} />
          </>
        ) : (
          <Stack sx={{ gap: 2.5 }}>
            <Subtitle>{t("Assistant.Invite.Login.CodeInfo", { email: invitation.maskedEmail })}</Subtitle>
            <FormDialog.Field errorMessage={requestCodeMutation.isError && t("Assistant.Invite.Login.SendCodeError")}>
              <Button variant="contained" fullWidth loading={requestCodeMutation.isPending} onClick={requestCode}>
                {t("Assistant.Invite.Login.SendCode")}
              </Button>
            </FormDialog.Field>
          </Stack>
        )}
      </Box>
    </InviteFrame>
  );
};

const CodeStep = ({ invitationId }: { invitationId: string }) => {
  const { t } = useTranslation();
  const id = useId();
  const [code, setCode] = useState("");
  const verifyCodeMutation = useMutation(verifyInvitationCodeMutationOptions(invitationId));

  const errorMessage = !verifyCodeMutation.error
    ? undefined
    : isInvalidCodeError(verifyCodeMutation.error)
      ? t("Assistant.Invite.Login.InvalidCode")
      : isInvitationClosedError(verifyCodeMutation.error)
        ? t("Assistant.Invite.Closed.Title")
        : t("Assistant.Invite.Login.Error");

  return (
    <form
      noValidate
      onSubmit={event => {
        event.preventDefault();
        if (code.length === CODE_LENGTH) verifyCodeMutation.mutate({ code });
      }}
    >
      <Stack sx={{ gap: 2.5 }}>
        <FormDialog.Field errorMessage={errorMessage}>
          <FormDialog.Label htmlFor={id}>{t("Assistant.Invite.Login.CodeLabel")}</FormDialog.Label>
          <TextField
            id={id}
            autoFocus
            value={code}
            onChange={event => {
              verifyCodeMutation.reset();
              setCode(event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH));
            }}
            size="small"
            fullWidth
            error={Boolean(errorMessage)}
            slotProps={{
              htmlInput: {
                "inputMode": "numeric",
                "autoComplete": "one-time-code",
                "maxLength": CODE_LENGTH,
                "data-1p-ignore": "true",
              },
            }}
          />
        </FormDialog.Field>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={code.length !== CODE_LENGTH}
          loading={verifyCodeMutation.isPending}
        >
          {t("Assistant.Invite.Login.SignIn")}
        </Button>
      </Stack>
    </form>
  );
};

const CLOSED_MESSAGES = {
  submitted: "Assistant.Invite.Closed.Submitted",
  expired: "Assistant.Invite.Closed.Expired",
  revoked: "Assistant.Invite.Closed.Revoked",
} as const;

export const InviteClosed = ({ invitation }: { invitation: AssistantInvitationInfo }) => {
  const { t } = useTranslation();
  const messageKey =
    invitation.status === "submitted" || invitation.status === "revoked"
      ? CLOSED_MESSAGES[invitation.status]
      : CLOSED_MESSAGES.expired;

  return (
    <InviteFrame title={t("Assistant.Invite.Closed.Title")}>
      <Subtitle>{t(messageKey, { inviter: invitation.inviterName })}</Subtitle>
    </InviteFrame>
  );
};

const InviteFrame = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack sx={{ height: "100dvh", justifyContent: "center", alignItems: "center" }}>
    <Stack sx={{ px: 2, transform: "translateY(-20%)", width: "min(550px, 100%)", gap: 1 }}>
      <Typography variant="h1" sx={{ fontSize: theme => theme.typography.pxToRem(28), fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Stack>
  </Stack>
);

const Subtitle = ({ children }: { children: ReactNode }) => (
  <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), color: theme => theme.palette.text.secondary }}>
    {children}
  </Typography>
);
